import {
  BillingItem,
  BillingItemAction,
  BillingItemActions,
  BillingItemBadge,
  BillingItemContent,
  BillingItemExtras,
  BillingItemList,
  BillingItemSubtitle,
  BillingItemTitle,
  BillingItemVisual,
} from 'billing/components/BillingItemList'
import BillingMethod from 'billing/models/BillingMethod'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { useConfirm } from 'components/ConfirmDialog'
import ConfirmSheet from 'components/ConfirmSheet'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownTitle,
} from 'components/Dropdown'
import Icon, { IconName } from 'components/Icon'
import { IconButton } from 'components/IconButton'
import IconVisual from 'components/IconVisual'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import Spinner from 'components/Spinner'
import { useTask } from 'hooks/mobx-hooks'
import { observer } from 'mobx-react'
import * as React from 'react'
import { match } from 'utils/pattern-match-util'
import { isChildrenEmpty } from 'utils/react-util'
import styles from './BillingMethodList.sass'

interface BillingMethodListProps {
  children?: React.ReactNode
  onAddClick?: () => unknown
  onAddDisabled?: boolean
}

/**
 * The BillingMethodList
 */
export function BillingMethodList({
  children,
  onAddClick,
  onAddDisabled,
}: BillingMethodListProps) {
  return (
    <>
      <BillingItemList>
        {children}
        {onAddClick && (
          <Button
            disabled={onAddDisabled}
            block
            type="text"
            onClick={onAddClick}
            data-test="add-new-payment-method-button"
          >
            Add New Payment Method
          </Button>
        )}
      </BillingItemList>
    </>
  )
}

interface BillingMethodListItemProps {
  billingMethod: BillingMethod
  selected?: boolean
  onClick?: () => unknown
  disabled?: boolean
  busy?: boolean
  readOnly?: boolean
  dropdownActions?: React.ReactNode
  ['data-test']?: string
}

export const BillingMethodListItem = observer(function BillingMethodListItem({
  billingMethod,
  selected,
  onClick,
  disabled,
  busy,
  readOnly,
  dropdownActions,
  'data-test': dataTest,
}: BillingMethodListItemProps) {
  const deleteModal = useConfirm()
  const deleteMethod = useTask.resolved(billingMethod.delete)
  const onRequestDelete = React.useCallback(() => {
    deleteModal.show(deleteMethod).then()
  }, [deleteModal, deleteMethod])
  busy = busy || deleteMethod.pending
  disabled = disabled || deleteMethod.pending
  return (
    <>
      <BillingItem onClick={onClick} disabled={disabled} data-test={dataTest}>
        <BillingMethodListItemDisplay billingMethod={billingMethod} />
        {!readOnly && (
          <>
            <BillingItemExtras>
              {selected && <BillingItemBadge>Current</BillingItemBadge>}
              {busy && <Spinner />}
              <BillingMethodItemActions
                selected={!!selected}
                busy={busy}
                disabled={disabled}
                billingMethod={billingMethod}
                onDeleteClick={onRequestDelete}
              >
                {dropdownActions}
              </BillingMethodItemActions>
            </BillingItemExtras>
          </>
        )}
      </BillingItem>
      <ConfirmSheet danger yes="Delete" no="Cancel" state={deleteModal}>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Delete Payment Method?
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Are you sure you want to delete this payment method?
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ConfirmSheet>
    </>
  )
})

interface BillingMethodItemActionsProps {
  busy: boolean
  billingMethod: BillingMethod
  onDeleteClick: () => void
  selected: boolean
  disabled: boolean
  children?: React.ReactNode
}

/**
 " Billing Method Item Actions
 */
const BillingMethodItemActions = observer(function BillingMethodItemActions({
  busy,
  billingMethod,
  onDeleteClick,
  children,
  selected,
  disabled,
}: BillingMethodItemActionsProps) {
  if (busy) {
    return null
  }

  const items = (
    <>
      {children}
      {billingMethod.allowedToDelete && (
        <DropdownItem
          onClick={onDeleteClick}
          icon={<Icon name="delete" />}
          disabled={selected}
        >
          <DropdownItemTitle>Delete</DropdownItemTitle>
          <DropdownItemDescription>
            {billingMethod.default || selected ? (
              <>You can't delete the selected payment method.</>
            ) : (
              <>Remove this payment method.</>
            )}
          </DropdownItemDescription>
        </DropdownItem>
      )}
    </>
  )

  if (isChildrenEmpty(items)) {
    return null
  }

  return (
    <BillingItemActions>
      <BillingItemAction>
        <Dropdown
          autoDismiss
          disabled={disabled}
          position="bottom-end"
          button={
            <IconButton>
              <Icon name="more" />
            </IconButton>
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          {items}
        </Dropdown>
      </BillingItemAction>
    </BillingItemActions>
  )
})

interface BillingMethodListItemDisplayProps {
  billingMethod: BillingMethod
}

const BillingMethodListItemDisplay = observer(
  function BillingMethodListItemDisplay({
    billingMethod,
  }: BillingMethodListItemDisplayProps) {
    const workspace = billingMethod.billingProfile.workspace
    switch (billingMethod.type) {
      case 'Credit': {
        return (
          <>
            <BillingItemVisual>
              <Avatar
                size={48}
                subject={{
                  avatar: workspace.branding.logos.icon,
                  displayName: '',
                }}
              />
            </BillingItemVisual>
            <BillingItemContent>
              <BillingItemTitle data-test="billing-method-title">
                {workspace.name} Credit
              </BillingItemTitle>
              <BillingItemSubtitle data-test="billing-method-subtitle">
                Statement Balance &nbsp;&nbsp;{' '}
                <Balance balance={billingMethod.balance} />
              </BillingItemSubtitle>
              <BillingItemSubtitle>
                Remaining Balance &nbsp;&nbsp;{' '}
                <Balance isRemaining balance={billingMethod.remainingBalance} />
              </BillingItemSubtitle>
            </BillingItemContent>
          </>
        )
      }

      case 'Stripe': {
        return (
          <>
            <BillingItemVisual>
              <Icon
                size={48}
                name={match<string, IconName>(billingMethod.brand, {
                  Visa: () => 'visa',
                  MasterCard: () => 'mastercard',
                  Discover: () => 'discover',
                  'American Express': () => 'amex',
                  [match.DEFAULT]: () => 'info',
                })}
              />
            </BillingItemVisual>
            <BillingItemContent>
              <BillingItemTitle data-test="billing-method-title">
                {billingMethod.brand}
              </BillingItemTitle>
              <BillingItemSubtitle data-test="billing-method-subtitle">
                Card no:{' '}
                <span className={styles.mono}>
                  **** **** **** {billingMethod.last4}
                </span>
              </BillingItemSubtitle>
              <BillingItemSubtitle>
                Expires: {billingMethod.expMonth}/{billingMethod.expYear}
              </BillingItemSubtitle>
            </BillingItemContent>
          </>
        )
      }
    }
    return (
      <>
        <BillingItemVisual>
          <IconVisual>
            <Icon name="info" />
          </IconVisual>
        </BillingItemVisual>
        <BillingItemContent>
          <BillingItemTitle data-test="billing-method-title">
            Unknown Payment Method "{billingMethod.type}"
          </BillingItemTitle>
          <BillingItemSubtitle>
            This version of the app does not recognize this payment type.
          </BillingItemSubtitle>
        </BillingItemContent>
      </>
    )
  },
)

interface BalanceProps {
  balance: number
  isRemaining?: boolean
}

function Balance({ balance, isRemaining }: BalanceProps) {
  return (
    <span
      className={
        isRemaining
          ? styles.remaining
          : balance > 0
            ? styles.positiveBalance
            : styles.negativeBalance
      }
    >
      {moneyFormatter.format(balance / 100)}
    </span>
  )
}

const moneyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'usd',
  style: 'currency',
})
